import { useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Check, Eye, EyeOff, Gift, LockKeyhole } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import type { z } from 'zod';
import { registerSchema } from '@shared/schemas';
import { useAuth } from '@/app/auth-context';
import { TurnstileWidget } from '@/components/auth/turnstile-widget';
import { PageLoading } from '@/components/feedback/page-loading';
import { Button, FieldError, FieldHint, Input, Label } from '@/components/ui';
import { getErrorMessage } from '@/lib/api';
import { isRegistrationConfigReady, usePublicConfig } from '@/lib/public-config';
import { routes, safeReturnTo } from '@/lib/routes';
import { usePageMeta } from '@/lib/seo';

type RegisterInput = z.input<typeof registerSchema>;
type RegisterOutput = z.output<typeof registerSchema>;

export function RegisterPage() {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const { register: createAccount, isAuthenticated, isLoading, user } = useAuth();
  const { config, isPending: configLoading, isUnavailable: configUnavailable } = usePublicConfig();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'), routes.intake);
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const onTurnstileToken = useCallback((token: string | null) => setTurnstileToken(token), []);
  usePageMeta({
    title: t('common:seo.registerTitle'),
    description: t('auth:register.subtitle'),
    noIndex: true,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<RegisterInput, unknown, RegisterOutput>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      fullName: '',
      email: '',
      password: '',
      preferredLanguage: i18n.resolvedLanguage?.startsWith('ar') ? 'ar' : 'en',
    },
  });

  if (isLoading) return <PageLoading />;
  if (isAuthenticated) {
    return (
      <Navigate
        to={
          user?.onboardingCompleted
            ? returnTo
            : `${routes.onboarding}?returnTo=${encodeURIComponent(returnTo)}`
        }
        replace
      />
    );
  }

  const challengeMisconfigured = config.turnstileRequired && !config.turnstileSiteKey;
  const registrationConfigReady = isRegistrationConfigReady({
    config,
    isPending: configLoading,
    isUnavailable: configUnavailable,
  });
  const onSubmit = async (values: RegisterOutput) => {
    if (configUnavailable) {
      setError('root', { message: t('auth:register.configUnavailable') });
      return;
    }
    if (config.turnstileRequired && !turnstileToken) {
      setError('root', { message: t('auth:register.challengeRequired') });
      return;
    }
    try {
      await createAccount({ ...values, turnstileToken: turnstileToken ?? undefined });
      void navigate(`${routes.onboarding}?returnTo=${encodeURIComponent(returnTo)}`, {
        replace: true,
      });
    } catch (error) {
      if (config.turnstileRequired) {
        setTurnstileToken(null);
        setTurnstileAttempt((attempt) => attempt + 1);
      }
      setError('root', { message: getErrorMessage(error, t('auth:register.error')) });
    }
  };

  return (
    <div className="auth-card auth-card--register">
      <p className="eyebrow">{t('auth:register.eyebrow')}</p>
      <h1>{t('auth:register.title')}</h1>
      <p className="auth-card__subtitle">{t('auth:register.subtitle')}</p>
      {config.freeDuringBeta && (
        <div className="beta-callout">
          <Gift size={20} />
          <div>
            <strong>{t('auth:register.betaTitle')}</strong>
            <p>{t('auth:register.betaBody')}</p>
          </div>
        </div>
      )}
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
        <div className="field-group">
          <Label htmlFor="register-name">{t('auth:register.fullName')}</Label>
          <Input
            id="register-name"
            autoComplete="name"
            placeholder={t('auth:register.namePlaceholder')}
            invalid={Boolean(errors.fullName)}
            {...register('fullName')}
          />
          <FieldError>{errors.fullName ? t('auth:validation.required') : undefined}</FieldError>
        </div>
        <div className="field-group">
          <Label htmlFor="register-email">{t('auth:common.email')}</Label>
          <Input
            id="register-email"
            type="email"
            autoComplete="email"
            placeholder={t('auth:register.emailPlaceholder')}
            invalid={Boolean(errors.email)}
            {...register('email')}
          />
          <FieldError>{errors.email ? t('auth:validation.email') : undefined}</FieldError>
        </div>
        <div className="field-group">
          <Label htmlFor="register-password">{t('auth:common.password')}</Label>
          <div className="password-field">
            <Input
              id="register-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              invalid={Boolean(errors.password)}
              {...register('password')}
            />
            <button
              type="button"
              className="password-toggle focus-ring"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={
                showPassword ? t('auth:common.hidePassword') : t('auth:common.showPassword')
              }
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <FieldHint>
            <Check size={13} /> {t('auth:register.passwordHint')}
          </FieldHint>
          <FieldError>{errors.password ? t('auth:validation.password') : undefined}</FieldError>
        </div>
        {configLoading && <p className="field-hint">{t('auth:common.working')}</p>}
        {config.turnstileRequired && config.turnstileSiteKey && (
          <TurnstileWidget
            key={turnstileAttempt}
            siteKey={config.turnstileSiteKey}
            onToken={onTurnstileToken}
          />
        )}
        {challengeMisconfigured && (
          <div className="form-alert" role="alert">
            {t('auth:register.challengeError')}
          </div>
        )}
        {configUnavailable && (
          <div className="form-alert" role="alert">
            {t('auth:register.configUnavailable')}
          </div>
        )}
        {errors.root?.message && (
          <div className="form-alert" role="alert">
            {errors.root.message}
          </div>
        )}
        <Button
          type="submit"
          size="lg"
          className="auth-submit"
          disabled={isSubmitting || !registrationConfigReady || challengeMisconfigured}
        >
          {isSubmitting ? t('auth:register.submitting') : t('auth:register.submit')}{' '}
          {!isSubmitting && <ArrowRight className="rtl-flip" size={18} />}
        </Button>
      </form>
      <p className="auth-agreement">
        {t('auth:register.agreement')} <Link to={routes.terms}>{t('common:footer.terms')}</Link> ·{' '}
        <Link to={routes.privacy}>{t('common:footer.privacy')}</Link>
      </p>
      <p className="auth-switch">
        {t('auth:register.hasAccount')}{' '}
        <Link to={`${routes.login}?returnTo=${encodeURIComponent(returnTo)}`}>
          {t('auth:register.signIn')}
        </Link>
      </p>
      <p className="auth-security">
        <LockKeyhole size={15} /> {t('auth:common.secure')}
      </p>
    </div>
  );
}

export default RegisterPage;
